import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { PostHogProvider } from '@posthog/react';

import React from 'react';
import { createRoot } from 'react-dom/client';
import './api/restClient';
import App from './App2';
import { initPosthog, trackPosthogRoutePageviews } from './common/posthog';
import getConfig from './config';
import { registerSiteDataPreview } from './devtools/siteDataPreview';
import { bootstrapPylonClientWidget, shouldBootstrapPylon } from './pylon-widget';
import { router } from './routes/router';
import layerStore from './store2/layers';

// Signal to the index.html early-error reporter that the bundle booted, so it stops its
// bundle-load watchdog and hands error tracking over to the real Bugsnag client below. CG-562.
(window as unknown as Record<string, unknown>).__cgAppBooted = true;

if (shouldBootstrapPylon(window.location.pathname)) {
  bootstrapPylonClientWidget();
}

const config = getConfig();

Bugsnag.start({
  apiKey: config.bugsnagApiKey,
  // A preview's stage is outside `enabledReleaseStages`, so previews report nothing.
  releaseStage: config.previewEnv ?? config.env,
  enabledReleaseStages: ['staging', 'production'],
  plugins: [new BugsnagPluginReact()],
  collectUserIp: false,
  onBreadcrumb: (breadcrumb) => {
    if (breadcrumb.type === 'request') {
      if (breadcrumb.metadata.request.includes('mapbox')) return false;
    }
  },
});
BugsnagPerformance.start({
  apiKey: config.bugsnagApiKey,
});
Bugsnag.addMetadata('env', { env: config.previewEnv ?? config.env });

const posthogClient = initPosthog(config.posthogApiKey, {
  api_host: config.posthogApiHost,
  ui_host: config.posthogUiHost,
});
trackPosthogRoutePageviews(router.history);

// Expose the PostHog client on window for debugging outside of production — e.g.
// posthog.featureFlags.getFlags() / reloadFeatureFlags() / overrideFeatureFlags(...).
if (config.env !== 'production') {
  (window as unknown as Record<string, unknown>).posthog = posthogClient;
}

const BugsnagErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

// Suppress layer-picker localStorage writebacks from the first tick — before the org-load
// (`setTOCLayersConfig`) or any picker mutation can run. The DB document is the source of truth,
// and the legacy keys must stay intact for the first-use seed migration.
layerStore.suppressLocalPersistence = true;

// window.cgSiteData(): regenerate the FME site-data GeoJSON for a project from the console. Just a
// function reference — the serializers are dynamically imported only when it is called.
registerSiteDataPreview();

const container = document.getElementById('root');
const root = createRoot(container!, {
  // React 19 no longer surfaces uncaught render/effect errors via window.onerror — wire them to Bugsnag directly.
  onUncaughtError: (error, errorInfo) => {
    const err = error instanceof Error ? error : new Error(String(error));
    Bugsnag.notify(err, (event) => {
      event.addMetadata('react.uncaught', errorInfo);
    });
  },
});

const render = () =>
  root.render(
    <PostHogProvider client={posthogClient}>
      <BugsnagErrorBoundary>
        <App />
      </BugsnagErrorBoundary>
    </PostHogProvider>,
  );

// Builds drop this import only while the condition reads `import.meta.env.DEV` directly.
if (import.meta.env.DEV && import.meta.env.VITE_FAKE_DRAWINGS === 'true') {
  import('./devtools/fakeDrawingServer/start')
    .then(({ startFakeDrawingServer }) => startFakeDrawingServer())
    .catch((error) => console.error('Fake drawings failed to start', error))
    .then(render);
} else {
  render();
}
